import { AfterViewInit, Component, ElementRef, EventEmitter, HostBinding, Injector, Input, NgZone, OnDestroy, Output, ViewChild, ChangeDetectionStrategy, Renderer2 } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { RiftBaseComponent } from '@rift/components/base/RiftBaseComponent';
import { Device } from '@rift/components/shared/viewport/devices/Device';
import { DeviceCollection } from '@rift/components/shared/viewport/devices/DeviceCollection';
import { Grid } from '@rift/components/shared/viewport/grid/Grid';
import { Line } from '@rift/components/shared/viewport/lines/Line';
import { PathMap } from '@rift/components/shared/viewport/pathmap/PathMap';
import { Polygon } from '@rift/components/shared/viewport/polygons/Polygon';
import { Register } from '@rift/components/shared/viewport/registers/Register';
import { RegisterCollection } from '@rift/components/shared/viewport/registers/RegisterCollection';
import { TargetCollection } from '@rift/components/shared/viewport/targets/TargetCollection';
import { ViewPortModeEnum } from '@rift/components/shared/viewport/ViewPortMode.Enum';
import { DeviceModel } from '@rift/models/restapi/Device.Model';
import { LineModel } from '@rift/models/restapi/Line.Model';
import { PolygonModel } from '@rift/models/restapi/Polygon.Model';
import { RegisterBaseModel } from '@rift/models/restapi/RegisterBase.Model';
import { TargetModel } from '@rift/models/websocket/Target.Model';
import { PathMapService } from '@rift/service/data/pathmap/PathMap.Service';
import { RecordingService } from '@rift/service/data/recording/Recording.Service';
import { UnitsOfMeasurementService } from '@rift/service/unitsofmeasurement/UnitsOfMeasurement.Service';
import { ViewPortLoadQueueService } from '@rift/service/viewport/ViewPort.LoadQueue.Service';
import { UnitsOfMeasurementEnum } from '@shared/enum/UnitsOfMeasurement.Enum';
import { DisplayItemMouseEvent } from '@shared/generic/canvas/DisplayItemMouseEvent';
import { IDisplayItem } from '@shared/generic/canvas/IDisplayItem';
import { ProcessMonitorServiceProcess } from '@shared/service/processmonitor/ProcessMonitor.Service.Process';
import { isNullOrUndefined } from '@shared/utility/General.Utility';
import { PointUtility } from '@shared/utility/Point.Utility';
import { fromEvent, Observable, Subject, Subscription, timer } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { MAX_CANVAS_FPS, GLOBAL_CACHE_SCALE } from '@rift/shared/Settings';

@Component({
    selector: 'rift-viewport',
    templateUrl: './ViewPort.Component.html',
    styleUrls: ['./ViewPort.Component.scss'],
})
export class ViewPortComponent extends RiftBaseComponent implements AfterViewInit, OnDestroy {
    public static className: string = 'ViewPortComponent';

    @Input()
    public fovClickEnabled: boolean = false;

    @Output()
    public lineUpdated: EventEmitter<Line> = new EventEmitter<Line>();

    @Output()
    public polygonUpdated: EventEmitter<Polygon> = new EventEmitter<Polygon>();
    public addDevicesProcess: ProcessMonitorServiceProcess;
    public addRegisterProcess: ProcessMonitorServiceProcess;
    public dOMMouseScrollProcess: ProcessMonitorServiceProcess;

    public getPathMapProcess: ProcessMonitorServiceProcess;

    @ViewChild('canvas', { static: true })
    public canvas: ElementRef<HTMLCanvasElement>;
    public loadQueueLoadedProcess: ProcessMonitorServiceProcess;
    public mouseDownProcess: ProcessMonitorServiceProcess;
    public mousewheelProcess: ProcessMonitorServiceProcess;

    @HostBinding()
    public id: string = 'rift-viewport';

    public onRequireStageUpdate: Subject<void> = new Subject<void>();
    public pressMoveProcess: ProcessMonitorServiceProcess;
    public pressUpProcess: ProcessMonitorServiceProcess;
    public requireStageUpdateProcess: ProcessMonitorServiceProcess;
    public showPathMapProcess: ProcessMonitorServiceProcess;

    private _dOMMouseScrollSub: Subscription = null;
    private _devices: DeviceCollection = null;
    private _getPathMapSub: Subscription = null;
    private _grid: Grid = null;
    private _height: number = 0;
    private _isMouseDown: boolean = false;
    private _lastDragLocation: createjs.Point = null;
    private _mode: ViewPortModeEnum = ViewPortModeEnum.view;
    private _mouseCordsContainer: createjs.Container;
    private _mouseCordsShape: createjs.Shape;
    private _mouseCordsText: createjs.Text;
    private _mouseDisabled: boolean = false;
    private _mouseDownSub: Subscription = null;
    private _mouseWheelSub: Subscription = null;
    private _pathMap: PathMap = null;
    private _pinchDistance: number = null;
    private _pressMoveSub: Subscription = null;
    private _pressUpSub: Subscription = null;
    private _registers: RegisterCollection = null;
    private _showMouseCords: boolean = false;
    private _showPathMap: boolean = false;
    private _showTargetsOverVideo: boolean = true;
    private _stage: createjs.StageGL = null;
    private _stageUpdateRequired: boolean = false;
    private _targets: TargetCollection = null;
    private _tickerListener: Function = null;
    private _unitOfMeasurement: UnitsOfMeasurementEnum = UnitsOfMeasurementEnum.metric;
    private _videoDevice: Device = null;
    private _width: number = 0;
    private _zoomContainer: createjs.Container = new createjs.Container();
    private _zoomHeight: number = Grid.Height;
    private _zoomRatio: number = 1;
    private _zoomWidth: number = Grid.Width;
    private readonly _ZoomIncrement: number = 0.08;
    private readonly _ZoomMax: number = 3.8;
    private readonly _ZoomMin: number = 0.4;

    public constructor(
        private readonly _renderBase: Renderer2,
        private readonly _unitsOfMeasurementService: UnitsOfMeasurementService,
        private readonly _dialog: MatDialog,
        private readonly _elementRef: ElementRef,
        private readonly _pathMapService: PathMapService,
        private readonly _recordingService: RecordingService,
        private readonly _loadQueue: ViewPortLoadQueueService,
        private readonly _zone: NgZone,
        private readonly _injector: Injector) {
        super(_injector, _dialog);

        this._zone.runOutsideAngular(() => {
            this.getPathMapProcess = this.processMonitorService.getProcess(ViewPortComponent.className, 'Getting path map');
            this.loadQueueLoadedProcess = this.processMonitorService.getProcess(ViewPortComponent.className, 'Load queue loaded');
            this.requireStageUpdateProcess = this.processMonitorService.getProcess(ViewPortComponent.className, 'Require stage update');
            this.loadDataProcess = this.processMonitorService.getProcess(ViewPortComponent.className, this.loadDataProcessText);
            this.addDevicesProcess = this.processMonitorService.getProcess(ViewPortComponent.className, 'Add Devices');
            this.addRegisterProcess = this.processMonitorService.getProcess(ViewPortComponent.className, 'Add Register');
            this.showPathMapProcess = this.processMonitorService.getProcess(ViewPortComponent.className, 'Show PathMap');
            this.mousewheelProcess = this.processMonitorService.getProcess(ViewPortComponent.className, 'Mouse wheel');
            this.dOMMouseScrollProcess = this.processMonitorService.getProcess(ViewPortComponent.className, 'DOM Mouse Scroll');
            this.mouseDownProcess = this.processMonitorService.getProcess(ViewPortComponent.className, 'Mouse Down');
            this.pressUpProcess = this.processMonitorService.getProcess(ViewPortComponent.className, 'Press Up');
            this.pressMoveProcess = this.processMonitorService.getProcess(ViewPortComponent.className, 'Press Move');

            if (this._loadQueue.isLoaded === true) {
                this.onLoadQueueComplete();
            } else {
                this.addSubscription(this.observableHandlerBase(this._loadQueue.loaded, this.loadQueueLoadedProcess).subscribe(() => this.onLoadQueueComplete()), this.loadQueueLoadedProcess);
            }

            this._registers = new RegisterCollection(this._zone, this._loadQueue);
            this._devices = new DeviceCollection(this._zone, this._loadQueue);
            this._grid = new Grid(this._zone, this._loadQueue);
            this._targets = new TargetCollection(this._zone, this._unitsOfMeasurementService, this._loadQueue);
            this._pathMap = new PathMap(this._zone);

            this.addSubscription(this.observableHandlerBase(this._grid.requireStageUpdate, this.requireStageUpdateProcess).subscribe(() => this.requireStageUpdate()), this.requireStageUpdateProcess);
            this.addSubscription(this.observableHandlerBase(this._devices.requireStageUpdate, this.requireStageUpdateProcess).subscribe(() => this.requireStageUpdate()), this.requireStageUpdateProcess);
            this.addSubscription(this.observableHandlerBase(this._registers.requireStageUpdate, this.requireStageUpdateProcess).subscribe(() => this.requireStageUpdate()), this.requireStageUpdateProcess);
            this.addSubscription(this.observableHandlerBase(this._targets.requireStageUpdate, this.requireStageUpdateProcess).subscribe(() => this.requireStageUpdate()), this.requireStageUpdateProcess);
            this.addSubscription(this.observableHandlerBase(this._pathMap.requireStageUpdate, this.requireStageUpdateProcess).subscribe(() => this.requireStageUpdate()), this.requireStageUpdateProcess);

            this._grid.mode = this.mode;
            this._devices.mode = this.mode;
            this._registers.mode = this.mode;
            this._targets.mode = this.mode;
            this._pathMap.mode = this.mode;

            this._zoomContainer.addChild(this._grid.container);
            this._zoomContainer.addChild(this._devices.container);
            this._zoomContainer.addChild(this._pathMap.container);
            this._zoomContainer.addChild(this._targets.container);
            this._zoomContainer.addChild(this._registers.container);

            createjs.Ticker.framerate = MAX_CANVAS_FPS;
        });

        this.initConnectionState();
    }

    public addDevices(devices: Array<DeviceModel>): Array<Device> {
        return this._zone.runOutsideAngular(() => {
            if (!isNullOrUndefined(devices)) {
                const addDevicesLength = devices.length;
                for (let i = 0; i < addDevicesLength; i++) {
                    this._devices.push(new Device(this._zone, this._loadQueue, devices[i]));
                }

                this.devices.sort((a, b) => a.deviceModel.x - b.deviceModel.x).forEach(device => {
                    device.video.mode = this.mode;
                    device.video.devices = this._devices;

                    this.addSubscription(this.observableHandlerBase(device.video.requireStageUpdate, this.addDevicesProcess).subscribe(() => this.requireStageUpdate()));

                    this._zoomContainer.addChildAt(device.video.container, this._zoomContainer.getChildIndex(this._grid.container) + 1);

                    if (device.deviceModel.master === true) {
                        this._pathMap.masterDevice = device;
                    }
                });

                return this._devices;
            }
        });
    }

    public addLifetimeTargets(targets: Array<TargetModel>): void {
        this._zone.runOutsideAngular(() => {
            this._targets.pushLifetimeTargets(targets);
            this.requireStageUpdate();
        });
    }

    public addPersistentTargets(frame: number, targets: Array<TargetModel>): void {
        this._zone.runOutsideAngular(() => {
            this._targets.pushPersistentTargets(frame, targets);
            this.requireStageUpdate();
        });
    }

    public addRegister(registerBaseModel: RegisterBaseModel, lines?: Array<LineModel>, polygons?: Array<PolygonModel>): void {
        this._zone.runOutsideAngular(() => {
            if (!this.isNullOrUndefined(registerBaseModel) && registerBaseModel.isViewPortVisibleInViewPort) {
                const register = new Register(this._zone, this._loadQueue, registerBaseModel);

                if (!this.isNullOrUndefined(registerBaseModel.lineIds)) {
                    const lineIdsLength = registerBaseModel.lineIds.length;
                    for (let index = 0; index < lineIdsLength; index++) {
                        const addLine = new Line(this._zone, register, this._loadQueue, lines.find(i => i.iD === registerBaseModel.lineIds[index]));
                        this.addSubscription(this.observableHandlerBase(addLine.updated, this.addRegisterProcess).subscribe((line: Line) => this.lineUpdated.next(line)), this.addRegisterProcess);
                        register.lines.push(addLine);
                    }
                }

                if (!this.isNullOrUndefined(registerBaseModel.polygonIds)) {
                    const polygonIdsLength = registerBaseModel.polygonIds.length;
                    for (let index = 0; index < polygonIdsLength; index++) {
                        const addPolygon = new Polygon(this._zone, register, this._loadQueue, polygons.find(i => i.iD === registerBaseModel.polygonIds[index]));
                        this.addSubscription(this.observableHandlerBase(addPolygon.updated, this.addRegisterProcess).subscribe((polygon: Polygon) => this.polygonUpdated.next(polygon)), this.addRegisterProcess);
                        register.polygons.push(addPolygon);
                    }
                }

                this._registers.push(register);
            }
        });
    }

    public addRegisters(registers: Array<RegisterBaseModel>, lines?: Array<LineModel>, polygons?: Array<PolygonModel>): void {
        this._zone.runOutsideAngular(() => {
            const registersLength = registers.length;
            for (let index = 0; index < registersLength; index++) {
                this.addRegister(registers[index], lines, polygons);
            }
        });
    }

    public bringToFront(displayItem: IDisplayItem): void {
        this._zone.runOutsideAngular(() => {
            if (displayItem instanceof Register) {
                this._registers.bringToFront(displayItem);
            }
        });
    }

    public center(): void {
        this._zone.runOutsideAngular(() => {
            this.zoomToPoint(this._zoomRatio, this.getCenter());
        });
    }

    public clear(): void {
        this._zone.runOutsideAngular(() => {
            if (!this.isNullOrUndefined(this._registers)) {
                this._registers.clear();
            }
            if (!this.isNullOrUndefined(this._devices)) {
                this._devices.clear();
            }
            if (!this.isNullOrUndefined(this._targets)) {
                this._targets.clear();
            }
        });
    }

    public get devices(): DeviceCollection {
        return this._devices;
    }

    public fillElement(element: ElementRef | HTMLElement): void {
        this._zone.runOutsideAngular(() => {
            const nativeElement = (element instanceof ElementRef ? element.nativeElement as HTMLElement : element);
            if (!this.isNullOrUndefined(nativeElement)) {
                const computedStyle = getComputedStyle(nativeElement);

                const paddingX = parseFloat(computedStyle.paddingLeft) + parseFloat(computedStyle.paddingRight);
                const paddingY = parseFloat(computedStyle.paddingTop) + parseFloat(computedStyle.paddingBottom);

                this.width = nativeElement.offsetWidth - paddingX;
                this.height = nativeElement.offsetHeight - paddingY;

                this.setCanvasWidthHeight();
            }
        });
    }
    public get height(): number {
        return this._height;
    }

    @Input()
    public set height(value: number) {
        this._zone.runOutsideAngular(() => {
            if (this._height !== value) {
                this._height = value;
                this.setCanvasWidthHeight();
            }
        });
    }
    public get mode(): ViewPortModeEnum {
        return this._mode;
    }

    @Input()
    public set mode(value: ViewPortModeEnum) {
        this._zone.runOutsideAngular(() => {
            if (this._mode !== value) {
                this._mode = value;
                this._registers.mode = value;
                this._devices.mode = value;
            }
        });
    }
    public get mouseDisabled(): boolean {
        return this._mouseDisabled;
    }

    @Input()
    public set mouseDisabled(value: boolean) {
        this._zone.runOutsideAngular(() => {
            if (this._mouseDisabled !== value) {
                this._mouseDisabled = value;
                if (value === true) {
                    this.removeMouseEventHandlers();
                } else {
                    this.addMouseEventHandlers();
                }
            }
        });
    }

    public ngAfterViewInit(): void {
        this._zone.runOutsideAngular(() => {
            this._devices.fovClickEnabled = this.fovClickEnabled;
            this._stage = new createjs.StageGL(this.canvas.nativeElement, { antialias: true });
            this._stage.enableMouseOver(20);
            createjs.Touch.enable(this._stage);

            this._stage.addChild(this._zoomContainer);

            this.addEventHandlers();

            this.setCanvasWidthHeight();
        });
    }

    public ngOnDestroy(): void {
        this._zone.runOutsideAngular(() => {
            this.removeEventHandlers();
            this._grid?.onDestroy();
            this._devices?.onDestroy();
            this._registers?.onDestroy();
            this._pathMap?.onDestroy();
            this._targets?.onDestroy();
            this._videoDevice?.onDestroy();
            if (!this.isNullOrUndefined(this._getPathMapSub)) {
                this._getPathMapSub.unsubscribe();
            }
            this._stage.tickEnabled = false;
            this._stage.enableMouseOver(0);
            createjs.Touch.disable(this._stage);
            this._stage.enableDOMEvents(false);
            this._stage.removeAllChildren();
            this._stage.uncache();
            this._stage.purgeTextures();
            this._stage = null;
            this._loadQueue.clearTargetCache();
            this._loadQueue.ngOnDestroy();
        });
        super.ngOnDestroy();
    }

    public pathMapRefresh(): Observable<void> {
        return this._zone.runOutsideAngular(() => {
            return this._pathMapService.getPathMap(this.getPathMapProcess).pipe(
                tap(result => {
                    this._pathMap.setPathMapModel(result);
                }),
                map(() => {
                    return;
                })
            );
        });
    }

    public get registers(): RegisterCollection {
        return this._registers;
    }

    public removePersistentTargets(windowStart: number, windowEnd: number): void {
        this._zone.runOutsideAngular(() => {
            this._targets.removePersistentTargets(windowStart, windowEnd);
            this.requireStageUpdate();
        });
    }

    public requireStageUpdate(): void {
        this._zone.runOutsideAngular(() => {
            this._stageUpdateRequired = true;
            this.onRequireStageUpdate.next();
        });
    }

    public setPersistentTargetsFrameNumber(frameNumber: number): void {
        this._targets.setPersistentFrameNumber(frameNumber);
    }

    public setVideoDevice(serialNumber: string): void {
        this._zone.runOutsideAngular(() => {
            this.devices.forEach(d => {
                if (!isNullOrUndefined(serialNumber) && serialNumber === d.deviceModel.serialNumber) {
                    this._videoDevice = d;
                    d.video.visible = true;
                } else {
                    d.video.visible = false;
                }
            });
        });
    }

    public get showMouseCords(): boolean {
        return this._showMouseCords;
    }
    public set showMouseCords(value: boolean) {
        this._showMouseCords = value;
        this.toggleMouseCords();
    }

    public get showPathMap(): boolean {
        return this._showPathMap;
    }
    @Input()
    public set showPathMap(value: boolean) {
        this._zone.runOutsideAngular(() => {
            if (this._showPathMap !== value) {
                this._showPathMap = value;
                if (this._showPathMap) {
                    this._getPathMapSub = this.addSubscription(timer(0, 5000).subscribe(() => {
                        this._pathMap.visible = true;
                        this.addSubscription(this.observableHandlerBase(this.pathMapRefresh(), this.showPathMapProcess).subscribe(), this.showPathMapProcess);
                    }));
                } else {
                    this._pathMap.visible = false;
                    if (!this.isNullOrUndefined(this._getPathMapSub)) {
                        this._getPathMapSub.unsubscribe();
                    }
                }
            }
        });
    }

    public get showTargetsOverVideo(): boolean {
        return this._showTargetsOverVideo;
    }

    public set showTargetsOverVideo(value: boolean) {
        this._showTargetsOverVideo = value;
        this._zoomContainer.removeChild(this.targets.container);
        if (value) {
            this._zoomContainer.addChildAt(this.targets.container, this._zoomContainer.getChildIndex(this._registers.container));
        } else {
            this._zoomContainer.addChildAt(this.targets.container, this._zoomContainer.getChildIndex(this._grid.container) + 1);
        }
    }

    public startVideoDevice(serialNumber: string): void {
        this._zone.runOutsideAngular(() => {
            this.devices.forEach(d => {
                if (!isNullOrUndefined(serialNumber) && serialNumber === d.deviceModel.serialNumber) {
                    d.video.visible = true;
                }
            });
        });
    }

    public stopVideoDevice(serialNumber: string): void {
        this._zone.runOutsideAngular(() => {
            this.devices.forEach(d => {
                if (!isNullOrUndefined(serialNumber) && serialNumber === d.deviceModel.serialNumber) {
                    d.video.visible = false;
                }
            });
        });
    }

    public get targets(): TargetCollection {
        return this._targets;
    }

    public get unitOfMeasurement(): UnitsOfMeasurementEnum {
        return this._unitOfMeasurement;
    }

    @Input()
    public set unitOfMeasurement(value: UnitsOfMeasurementEnum) {
        this._zone.runOutsideAngular(() => {
            if (this._unitOfMeasurement !== value) {
                this._unitOfMeasurement = value;
                this._grid.unitOfMeasurement = value;
                this._targets.unitOfMeasurement = value;
            }
        });
    }

    public update(): void {
        this._zone.runOutsideAngular(() => {
            this._registers.update();
            this._devices.update();
            this._grid.update();
        });
    }

    public get videoDevice(): Device {
        return this._videoDevice;
    }
    public get width(): number {
        return this._width;
    }

    @Input()
    public set width(value: number) {
        this._zone.runOutsideAngular(() => {
            if (this._width !== value) {
                this._width = value;
                this.setCanvasWidthHeight();
            }
        });
    }

    public zoomIncrement(increment: number, centerOnMouse: boolean): void {
        this._zone.runOutsideAngular(() => {
            if (this.isNullOrUndefined(increment)) {
                return;
            }

            this.zoom(this._zoomRatio + increment, centerOnMouse);
        });
    }

    public zoomTo(ratio: number, centerOnMouse: boolean): void {
        this._zone.runOutsideAngular(() => {
            if (this.isNullOrUndefined(ratio)) {
                return;
            }

            this.zoom(ratio, centerOnMouse);
        });
    }

    private addEventHandlers(): void {
        this._zone.runOutsideAngular(() => {
            this._tickerListener = createjs.Ticker.on('tick', this.onTick.bind(this));

            if (this.isNullOrUndefined(this._mouseDisabled) || this._mouseDisabled === false) {
                this.addMouseEventHandlers();
            }
        });
    }

    private addMouseEventHandlers(): void {
        this._zone.runOutsideAngular(() => {
            this._mouseWheelSub = this.addSubscription(this.observableHandlerBase(fromEvent(this._stage.canvas as HTMLElement, 'mousewheel'), this.mousewheelProcess).subscribe(event => { this.onCanvasWheel(event as MouseEvent); }), this.mousewheelProcess);
            this._dOMMouseScrollSub = this.addSubscription(this.observableHandlerBase(fromEvent(this._stage.canvas as HTMLElement, 'DOMMouseScroll'), this.dOMMouseScrollProcess).subscribe(event => { this.onCanvasWheel(event as MouseEvent); }), this.dOMMouseScrollProcess);

            this._mouseDownSub = this.addSubscription(this.observableHandlerBase(this._grid.mouseDown, this.mouseDownProcess).subscribe(event => { this.onGridMouseDown(event); }), this.mouseDownProcess);
            this._pressMoveSub = this.addSubscription(this.observableHandlerBase(this._grid.pressMove, this.pressMoveProcess).subscribe(event => { this.onGridPressMove(event); }), this.pressMoveProcess);
            this._pressUpSub = this.addSubscription(this.observableHandlerBase(this._grid.pressUp, this.pressUpProcess).subscribe(event => { this.onGridPressUp(event); }), this.pressUpProcess);
        });
    }

    private getCenter(): createjs.Point {
        return this._zone.runOutsideAngular(() => {
            let devicesLength = 0;

            if (!this.isNullOrUndefined(this._devices)) {
                devicesLength = this._devices.length;
            }

            if (devicesLength === 0 || this.mode === ViewPortModeEnum.deviceGraphics) {
                return this.getCenterGrid();
            } else {
                return this.getCenterFieldOfView();
            }
        });
    }

    private getCenterFieldOfView(): createjs.Point {
        return this._zone.runOutsideAngular(() => {
            const bounds = this.getFieldOfViewBounds();
            const zoomContainerCenter = this.getZoomContainerCenter();

            if (!this.isNullOrUndefined(bounds) && !this.isNullOrUndefined(zoomContainerCenter)) {
                const aX = ((Grid.Width / 2) - bounds.x);
                const bX = bounds.width - aX;
                const cX = bX - aX;
                const dX = zoomContainerCenter.x - (cX / 2);

                const aY = ((Grid.Height / 2) - bounds.y);
                const bY = bounds.height - aY;
                const cY = bY - aY;
                const dY = zoomContainerCenter.y - (cY / 2);

                return new createjs.Point(dX, dY);
            }
        });
    }

    private getCenterGrid(): createjs.Point {
        return this._zone.runOutsideAngular(() => {
            return this.getZoomContainerCenter();
        });
    }

    private getFieldOfViewBounds(): createjs.Rectangle {
        return this._zone.runOutsideAngular(() => {
            const devicesLength = this._devices.length;
            const points: createjs.Point[] = [];
            for (let deviceIndex = 0; deviceIndex < devicesLength; deviceIndex++) {
                const device = this._devices[deviceIndex];
                if (!this.isNullOrUndefined(device)) {
                    const deviceBounds = device.getBounds();
                    if (!this.isNullOrUndefined(deviceBounds)) {
                        points.push(device.container.localToLocal(deviceBounds.x, deviceBounds.y, this._zoomContainer));
                        points.push(device.container.localToLocal(deviceBounds.x, deviceBounds.y + deviceBounds.height, this._zoomContainer));
                        points.push(device.container.localToLocal(deviceBounds.x + deviceBounds.width, deviceBounds.y + deviceBounds.height, this._zoomContainer));
                        points.push(device.container.localToLocal(deviceBounds.x + deviceBounds.width, deviceBounds.y, this._zoomContainer));
                    }
                }
            }

            return PointUtility.getBounds(points);
        });
    }

    private getZoomContainerCenter(): createjs.Point {
        return this._zone.runOutsideAngular(() => {
            if (!this.isNullOrUndefined(this.width) && !this.isNullOrUndefined(this._zoomWidth) && !this.isNullOrUndefined(this.height) && !this.isNullOrUndefined(this._zoomHeight)) {
                return new createjs.Point((this.width / 2) - (this._zoomWidth / 2), (this.height / 2) - (this._zoomHeight / 2));
            }
        });
    }

    private onCanvasWheel(event: MouseEvent): void {
        this._zone.runOutsideAngular(() => {
            event.preventDefault();
            const increment = (Math.max(-1, Math.min(1, ((event as any).wheelDelta || -event.detail))) > 0) ? this._ZoomIncrement : -this._ZoomIncrement;
            this.zoomIncrement(increment, true);
        });
    }

    private onGridMouseDown(event: DisplayItemMouseEvent): void {
        this._zone.runOutsideAngular(() => {
            this._isMouseDown = true;
            this._lastDragLocation = new createjs.Point(event.mouseEvent.stageX, event.mouseEvent.stageY);
        });
    }

    private onGridPressMove(event: DisplayItemMouseEvent): void {
        this._zone.runOutsideAngular(() => {
            if (!this.isNullOrUndefined((event.mouseEvent.nativeEvent as any).touches) && (event.mouseEvent.nativeEvent as any).touches.length === 2) {
                const nativeEvent = (event.mouseEvent.nativeEvent as any);
                const touch1 = nativeEvent.touches.item(0);
                const touch2 = nativeEvent.touches.item(1);

                const dx = touch1.clientX - touch2.clientX;
                const dy = touch2.clientY - touch1.clientY;
                const distance = Math.sqrt(dx * dx + dy * dy);

                if (!this.isNullOrUndefined(this._pinchDistance)) {
                    if (distance > this._pinchDistance) {
                        this.zoomIncrement(0.02, false);
                    } else if (distance < this._pinchDistance) {
                        this.zoomIncrement(-0.02, false);
                    }
                }

                this._pinchDistance = distance;
            } else {
                if (this._isMouseDown) {
                    this._zoomContainer.x = this._zoomContainer.x - (this._lastDragLocation.x - event.mouseEvent.stageX);
                    this._zoomContainer.y = this._zoomContainer.y - (this._lastDragLocation.y - event.mouseEvent.stageY);

                    this._lastDragLocation = new createjs.Point(event.mouseEvent.stageX, event.mouseEvent.stageY);
                }

                this._pinchDistance = null;
            }

            this.requireStageUpdate();
        });
    }

    private onGridPressUp(event: DisplayItemMouseEvent): void {
        this._zone.runOutsideAngular(() => {
            this._isMouseDown = false;
            this._lastDragLocation = null;
            this._pinchDistance = null;
        });
    }

    private onLoadQueueComplete(): void {
        this._zone.runOutsideAngular(() => {
            this.center();
        });
    }

    private onStageMouseMove(event: createjs.MouseEvent): void {
        const offsetX = 20;
        const offsetY = 20;
        const textPadding = 3;

        const localPoint = new createjs.Point(event.localX, event.localY);
        const gridPoint = this._stage.localToLocal(localPoint.x, localPoint.y, this._grid.container);
        const gpPoint = new createjs.Point(gridPoint.x - (Grid.Width / 2), gridPoint.y - (Grid.Height / 2));

        this._mouseCordsText.x = textPadding;
        this._mouseCordsText.y = textPadding;

        this._mouseCordsContainer.x = localPoint.x + offsetX;
        this._mouseCordsContainer.y = localPoint.y + offsetY;

        this._mouseCordsText.text = `X:${gpPoint.x.toFixed(2)},Y:${gpPoint.y.toFixed(2)}`;
        const textWidth = this._mouseCordsText.getMeasuredWidth();
        const textHeight = this._mouseCordsText.getMeasuredHeight();
        this._mouseCordsText.cache(0, 0, textWidth, textHeight, GLOBAL_CACHE_SCALE);

        const shapeWidth = textWidth + (2 * textPadding);
        const shapeHeight = textHeight + (2 * textPadding);

        this._mouseCordsShape.graphics.beginFill('#ffffff');
        this._mouseCordsShape.graphics.drawRoundRect(0, 0, shapeWidth, shapeHeight, 3);
        this._mouseCordsShape.cache(0, 0, shapeWidth, shapeHeight, GLOBAL_CACHE_SCALE);

        this.requireStageUpdate();
    }

    private onTick(event: createjs.TickerEvent): void {
        this._zone.runOutsideAngular(() => {
            if (this._stageUpdateRequired === true) {
                try {
                    this._stage.update();
                    this._stageUpdateRequired = false;
                } catch { }
            }
        });
    }

    private removeEventHandlers(): void {
        this._zone.runOutsideAngular(() => {
            createjs.Ticker.off('tick', this._tickerListener);
        });
    }

    private removeMouseEventHandlers(): void {
        this._zone.runOutsideAngular(() => {
            if (!this.isNullOrUndefined(this._mouseWheelSub)) {
                this._mouseWheelSub.unsubscribe();
            }
            if (!this.isNullOrUndefined(this._dOMMouseScrollSub)) {
                this._dOMMouseScrollSub.unsubscribe();
            }

            if (!this.isNullOrUndefined(this._mouseDownSub)) {
                this._mouseDownSub.unsubscribe();
            }
            if (!this.isNullOrUndefined(this._pressMoveSub)) {
                this._pressMoveSub.unsubscribe();
            }
            if (!this.isNullOrUndefined(this._pressUpSub)) {
                this._pressUpSub.unsubscribe();
            }
        });
    }

    private setCanvasWidthHeight(): void {
        this._zone.runOutsideAngular(() => {
            if (!this.isNullOrUndefined(this.canvas) && !this.isNullOrUndefined(this.canvas.nativeElement) && !this.isNullOrUndefined(this.width) && !this.isNullOrUndefined(this.height)) {
                this._renderBase.setProperty(this.canvas.nativeElement, 'width', this.width.toString());
                this._renderBase.setProperty(this.canvas.nativeElement, 'height', this.height.toString());

                if (!this.isNullOrUndefined(this._stage) && this._stage instanceof createjs.StageGL) {
                    this._stage.updateViewport(this.width, this.height);
                }

                this.center();

                this.requireStageUpdate();
            }
        });
    }

    private toggleMouseCords(): void {
        if (this.isNullOrUndefined(this._mouseCordsText) || this.isNullOrUndefined(this._mouseCordsContainer) || this.isNullOrUndefined(this._mouseCordsShape)) {
            this._mouseCordsContainer = new createjs.Container();

            this._mouseCordsShape = new createjs.Shape();
            this._mouseCordsContainer.addChild(this._mouseCordsShape);

            this._mouseCordsText = new createjs.Text('', '14px Arial');
            this._mouseCordsContainer.addChild(this._mouseCordsText);
        }

        if (!this.isNullOrUndefined(this._showMouseCords) && this._showMouseCords === true) {
            this._stage.addChild(this._mouseCordsContainer);
            this._stage.on('stagemousemove', this.onStageMouseMove.bind(this));
        } else {
            this._stage.removeChild(this._mouseCordsContainer);
            this._stage.off('stagemousemove', this.onStageMouseMove.bind(this));
        }
    }

    private zoom(ratio: number, centerOnMouse: boolean): void {
        this._zone.runOutsideAngular(() => {
            const min_ratio = this._ZoomMin;
            const max_ratio = this._ZoomMax;

            if (ratio < min_ratio) {
                ratio = min_ratio;
            }

            if (ratio > max_ratio) {
                ratio = max_ratio;
            }

            // zoom center point
            if (centerOnMouse === true) {
                this.zoomToMouse(ratio, new createjs.Point(this._stage.mouseX, this._stage.mouseY));
            } else {
                this.zoomToPoint(ratio, this.getCenter());
                this.center();
            }
        });
    }

    private zoomToMouse(zoomRatio: number, mousePoint: createjs.Point): void {
        this._zone.runOutsideAngular(() => {
            if (this.isNullOrUndefined(zoomRatio)) {
                return;
            }

            // percentages from side
            const pXZoom = ((this._zoomContainer.x * -1) + mousePoint.x) * 100 / this._zoomWidth;
            const pYZoom = ((this._zoomContainer.y * -1) + mousePoint.y) * 100 / this._zoomHeight;

            // update ratio and dimensions
            this._zoomRatio = zoomRatio;
            this._zoomWidth = Grid.Width * this._zoomRatio;
            this._zoomHeight = Grid.Height * this._zoomRatio;

            // translate view back to center point
            const xZoom = ((this._zoomWidth * pXZoom / 100) - mousePoint.x);
            const yZoom = ((this._zoomHeight * pYZoom / 100) - mousePoint.y);

            this._zoomContainer.x = xZoom * -1;
            this._zoomContainer.y = yZoom * -1;
            this._zoomContainer.scaleX = this._zoomRatio;
            this._zoomContainer.scaleY = this._zoomRatio;

            this.requireStageUpdate();
        });
    }

    private zoomToPoint(zoomRatio: number, point: createjs.Point): void {
        this._zone.runOutsideAngular(() => {
            if (this.isNullOrUndefined(zoomRatio)) {
                return;
            }

            // update ratio and dimensions
            this._zoomRatio = zoomRatio;
            this._zoomWidth = Grid.Width * this._zoomRatio;
            this._zoomHeight = Grid.Height * this._zoomRatio;

            this._zoomContainer.x = point.x;
            this._zoomContainer.y = point.y;
            this._zoomContainer.scaleX = this._zoomRatio;
            this._zoomContainer.scaleY = this._zoomRatio;

            this.requireStageUpdate();
        });
    }
}
